import { LscApiError } from './api';
import type { LscAdminSupportIssue, LscSupportIssueStatus } from './types';
import {
  assertCustomerSafeSupportIssueCopy,
  sanitizeSupportIssueContext,
  supportIssueCategoryLabel,
  supportIssueErrorMessage,
  supportIssueSeverityLabel,
} from './supportIssueCopy';
import { buildInternalCopyPattern } from './internalSanitizerPatterns';

export const LSC_ADMIN_SHELL_TITLE = 'LSC Admin';
export const LSC_ADMIN_SHELL_INTRO =
  'Internal support triage for League Stream Center. This area is for authorized Scoutlytics operators only.';
export const LSC_ADMIN_BLOCKED_TITLE = 'Admin access required';
export const LSC_ADMIN_BLOCKED_MESSAGE =
  'You do not have access to LSC admin tools. Contact Scoutlytics support if you believe this is an error.';
export const LSC_ADMIN_ISSUE_QUEUE_TITLE = 'Support issues';
export const LSC_ADMIN_ISSUE_QUEUE_INTRO =
  'Review customer-submitted reports. Updates here are triage notes only and do not change league billing, schedule, or archive state.';
export const LSC_ADMIN_ISSUE_EMPTY = 'No support issues match the current filters.';
export const LSC_ADMIN_ISSUE_SAVE_CTA = 'Save triage updates';
export const LSC_ADMIN_ISSUE_SAVE_SUCCESS = 'Triage updates saved.';
export const LSC_ADMIN_COPY_DIAGNOSTIC_CTA = 'Copy safe diagnostic bundle';
export const LSC_ADMIN_COPY_DIAGNOSTIC_SUCCESS = 'Diagnostic bundle copied.';
export const LSC_ADMIN_NO_MUTATION_NOTE =
  'This dashboard does not change billing, schedule, live verification, archive, setup progress, env, or Stripe state.';

const STATUS_LABELS: Record<LscSupportIssueStatus, string> = {
  open: 'Open',
  triaged: 'Triaged',
  investigating: 'Investigating',
  waiting_on_customer: 'Waiting on customer',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

const INTERNAL_COPY_PATTERN = buildInternalCopyPattern(
  'firestore|cloud run|webhook|stripe payload|oauth|api_key|secret|token|operator|env_|__ENV_PREFIX__|raw payload|session cookie|allowlist'
);

export function adminIssueStatusLabel(status: LscSupportIssueStatus | string): string {
  return STATUS_LABELS[status as LscSupportIssueStatus] ?? 'Unknown';
}

export function parseAdminIssueError(err: unknown, fallback?: string): string {
  if (err instanceof LscApiError) {
    if (err.status === 403 || err.code === 'admin_forbidden') {
      return LSC_ADMIN_BLOCKED_MESSAGE;
    }
    if (err.code === 'issue_not_found') {
      return supportIssueErrorMessage('issue_not_found');
    }
    return supportIssueErrorMessage(err.code, err.message);
  }
  if (err instanceof Error && err.message && !INTERNAL_COPY_PATTERN.test(err.message)) {
    return err.message;
  }
  return fallback ?? supportIssueErrorMessage(undefined);
}

export function isAdminAccessBlockedError(err: unknown): boolean {
  return (
    err instanceof LscApiError &&
    (err.status === 403 || err.code === 'admin_forbidden')
  );
}

export function compactLeagueId(leagueId?: string | null): string {
  const value = String(leagueId || '').trim();
  if (!value) return '—';
  if (value.length <= 12) return value;
  return `${value.slice(0, 8)}…`;
}

export function formatAdminIssueDiagnosticBundle(issue: LscAdminSupportIssue): string {
  const context = sanitizeSupportIssueContext(issue.context ?? {});
  const bundle = {
    issue_id: issue.issue_id,
    status: issue.status,
    category: issue.category,
    severity: issue.severity,
    surface: issue.surface,
    league_id: issue.league_id,
    reporter_role: issue.reporter_role,
    created_at: issue.created_at,
    updated_at: issue.updated_at,
    context,
  };
  return JSON.stringify(bundle, null, 2);
}

export function assertAdminIssueCopySafe(text: string): boolean {
  return assertCustomerSafeSupportIssueCopy(text) && !INTERNAL_COPY_PATTERN.test(text);
}

export function adminIssueSummaryLine(issue: LscAdminSupportIssue): string {
  return `${supportIssueCategoryLabel(issue.category)} · ${supportIssueSeverityLabel(issue.severity)} · ${adminIssueStatusLabel(issue.status)}`;
}

export function adminIssueListColumns(): string[] {
  return ['Reference', 'Title', 'Category', 'Severity', 'Status', 'League', 'Reporter role', 'Updated'];
}

export const ADMIN_FORBIDDEN_CONTROL_LABELS = [
  'Firestore',
  'Cloud Run',
  'Stripe subscription',
  'Scheduler',
  'Cloud Tasks',
  'Force schedule apply',
  'VOD backfill',
  'Ownership transfer',
  'Impersonate',
  'Env editor',
] as const;
